import { MousePointerClick } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PessoaResumoDTO } from "@/types/pessoa";
import { getInitials } from "@/utils/initials-format";
import { cn } from "@/lib/utils";

interface PersonPreviewCardProps {
  pessoa: PessoaResumoDTO;
  /** Indica que clicar abre o perfil completo (mostra a dica no rodapé). */
  isClickable?: boolean;
}

/**
 * Conteúdo do hover preview de uma pessoa: avatar, nome e email, com dica de
 * clique. Puramente apresentacional — usa apenas o `pessoa` já carregado no
 * contexto, sem disparar requisições.
 */
export function PersonPreviewCard({ pessoa, isClickable }: PersonPreviewCardProps) {
  const foto = pessoa.foto || pessoa.fotoThumb;
  const isExColaborador = !pessoa.ativo;

  return (
    <div className="p-4">
      <div className="flex items-center gap-3">
        <Avatar
          className={cn("h-12 w-12 border border-border shadow-sm", isExColaborador && "grayscale")}
        >
          {foto && <AvatarImage src={foto} alt={pessoa.nome} className="object-cover" />}
          <AvatarFallback className="bg-primary/10 text-base font-semibold text-primary">
            {getInitials(pessoa.nome)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground" title={pessoa.nome}>
            {pessoa.nome}
          </p>
          <p className="truncate text-xs text-muted-foreground" title={pessoa.email}>
            {pessoa.email}
          </p>
        </div>
      </div>

      {isClickable && (
        <p className="mt-3 flex items-center gap-1.5 border-t border-border/60 pt-2.5 text-[11px] text-muted-foreground">
          <MousePointerClick className="h-3 w-3" />
          Clique para ver o perfil completo
        </p>
      )}
    </div>
  );
}
